import React, { useState, useEffect } from 'react';
import { financeAPI } from '../../lib/api';
import { 
    Plus, Edit, Trash2, Search, RefreshCw, ChevronRight, ChevronDown,
    GripVertical, Folder, FolderOpen
} from 'lucide-react';

const ChartOfAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingId, setEditingId] = useState(null);
    
    // Drag and drop state
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);
    const [expandedGroups, setExpandedGroups] = useState({});

    // Form data - simplified
    const [formData, setFormData] = useState({
        account_code: '',
        account_name: '',
        account_type: '',
        parent_account_id: '',
        description: '',
        is_active: true
    });

    const accountTypes = [
        { value: 'Asset', label: 'Assets', color: 'blue' },
        { value: 'Liability', label: 'Liabilities', color: 'red' },
        { value: 'Equity', label: 'Equity', color: 'purple' },
        { value: 'Revenue', label: 'Income/Revenue', color: 'green' },
        { value: 'Expense', label: 'Expenses', color: 'orange' }
    ];

    const levelNames = {
        1: 'Primary Category',
        2: 'Sub Category',
        3: 'Group',
        4: 'Account Head'
    };

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const response = await financeAPI.getChartOfAccounts(1, { search: '' });
            const responseData = response.data;
            const accountsData = responseData?.data?.data || responseData?.data || [];
            setAccounts(Array.isArray(accountsData) ? accountsData : []);
        } catch (err) {
            console.error('Failed to load accounts:', err);
            setError('Failed to load chart of accounts');
            setAccounts([]);
        }
        finally { setLoading(false); }
    };

    // Build hierarchical tree structure
    const buildHierarchy = () => {
        const hierarchy = {};
        
        // Initialize with account types
        accountTypes.forEach(type => {
            hierarchy[type.value] = {
                id: type.value,
                account_name: type.label,
                account_code: '',
                account_type: type.value,
                level: 1,
                children: [],
                isCategory: true
            };
        });

        // Add accounts to hierarchy
        accounts.forEach(account => {
            const type = account.account_type || 'Other';
            if (!hierarchy[type]) {
                hierarchy[type] = {
                    id: type,
                    account_name: type,
                    account_code: '',
                    account_type: type,
                    level: 1,
                    children: [],
                    isCategory: true
                };
            }
            
            const node = { ...account, children: [] };
            
            if (!account.parent_account_id) {
                // Top level (Level 2 in our 4-level system)
                hierarchy[type].children.push(node);
            } else {
                // Find parent and add to children
                const parent = findParent(accounts, account.parent_account_id);
                if (parent) {
                    // Add to parent's children array
                    addToParent(hierarchy[type].children, parent.id, node);
                } else {
                    // Parent not found, add to top level
                    hierarchy[type].children.push(node);
                }
            }
        });

        return hierarchy;
    };

    const findParent = (accounts, parentId) => {
        return accounts.find(a => a.id === parentId);
    };

    const addToParent = (children, parentId, node) => {
        for (let child of children) {
            if (child.id === parentId) {
                child.children.push(node);
                return true;
            }
            if (child.children && addToParent(child.children, parentId, node)) {
                return true;
            }
        }
        return false;
    };

    const hierarchy = buildHierarchy();

    // Drag and Drop handlers
    const handleDragStart = (e, account) => {
        setDraggedItem(account);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', JSON.stringify(account));
    };

    const handleDragOver = (e, account) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (draggedItem && draggedItem.id !== account.id) {
            setDragOverItem(account);
        }
    };

    const handleDragLeave = () => {
        setDragOverItem(null);
    };

    const handleDrop = async (e, targetAccount) => {
        e.preventDefault();
        
        if (!draggedItem || draggedItem.id === targetAccount.id) {
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        // Calculate new level based on target
        const newLevel = targetAccount.level + 1;
        if (newLevel > 4) {
            alert('Maximum hierarchy level is 4');
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        // Prevent dropping parent into its own child
        if (isDescendant(draggedItem, targetAccount)) {
            alert('Cannot move an account into its own child');
            setDraggedItem(null);
            setDragOverItem(null);
            return;
        }

        try {
            await financeAPI.updateChartOfAccount(draggedItem.id, {
                ...draggedItem,
                parent_account_id: targetAccount.id,
                level: newLevel
            });
            
            await fetchAccounts();
        } catch (err) {
            console.error('Failed to update account hierarchy:', err);
            setError('Failed to reorganize account');
        }

        setDraggedItem(null);
        setDragOverItem(null);
    };

    const isDescendant = (parent, target) => {
        // Check if target is a descendant of parent
        const findInChildren = (children, parentId) => {
            for (let child of children) {
                if (child.id === parentId) return true;
                if (child.children && findInChildren(child.children, parentId)) return true;
            }
            return false;
        };
        
        // Check if draggedItem is ancestor of target
        const targetAccount = findAccount(accounts, target.id);
        if (targetAccount) {
            return findInChildren(getAllChildren(targetAccount), parent.id);
        }
        return false;
    };

    const findAccount = (list, id) => {
        for (let item of list) {
            if (item.id === id) return item;
        }
        return null;
    };

    const getAllChildren = (account) => {
        const children = [];
        accounts.forEach(acc => {
            if (acc.parent_account_id === account.id) {
                children.push(acc);
                children.push(...getAllChildren(acc));
            }
        });
        return children;
    };

    // Drag to top level (make it a root account)
    const handleDropToRoot = async (e, category) => {
        e.preventDefault();
        
        if (!draggedItem) return;

        try {
            await financeAPI.updateChartOfAccount(draggedItem.id, {
                ...draggedItem,
                parent_account_id: null,
                level: 2 // Top level under category
            });
            
            await fetchAccounts();
        } catch (err) {
            console.error('Failed to update account:', err);
            setError('Failed to move account');
        }

        setDraggedItem(null);
        setDragOverItem(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.account_name || !formData.account_code) {
            alert('Please enter Account Name and Code');
            return;
        }

        let submitData = {
            ...formData,
            level: formData.parent_account_id ? (getAccountLevel(formData.parent_account_id) + 1) : 2,
            parent_account_id: formData.parent_account_id || null
        };

        try {
            if (editingId) {
                await financeAPI.updateChartOfAccount(editingId, submitData);
            } else {
                await financeAPI.createChartOfAccount(submitData);
            }
            setShowModal(false);
            resetForm();
            fetchAccounts();
        } catch (err) {
            console.error('Error saving account:', err);
            setError(err.response?.data?.message || 'Failed to save account');
        }
    };

    const getAccountLevel = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        return account?.level || 1;
    };

    const handleEdit = (account) => {
        setFormData({
            account_code: account.account_code || '',
            account_name: account.account_name || '',
            account_type: account.account_type || '',
            parent_account_id: account.parent_account_id || '',
            description: account.description || '',
            is_active: account.is_active ?? true
        });
        setEditingId(account.id);
        setShowModal(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this account? This will also delete all child accounts.')) return;
        try {
            await financeAPI.deleteChartOfAccount(id);
            fetchAccounts();
        } catch (err) {
            setError('Failed to delete account');
        }
    };

    const resetForm = () => {
        setFormData({
            account_code: '',
            account_name: '',
            account_type: '',
            parent_account_id: '',
            description: '',
            is_active: true
        });
        setEditingId(null);
    };

    const toggleExpand = (id) => {
        setExpandedGroups(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    // Render tree item recursively
    const renderTreeItem = (account, depth = 0) => {
        const hasChildren = account.children && account.children.length > 0;
        const isExpanded = expandedGroups[account.id];
        const isDragging = draggedItem?.id === account.id;
        const isDragOver = dragOverItem?.id === account.id;
        const canDrop = draggedItem && draggedItem.id !== account.id && !isDescendant(draggedItem, account);

        return (
            <div key={account.id} className={`${depth > 0 ? 'ml-6' : ''}`}>
                <div 
                    draggable={!account.isCategory}
                    onDragStart={(e) => !account.isCategory && handleDragStart(e, account)}
                    onDragOver={(e) => canDrop && handleDragOver(e, account)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => canDrop && handleDrop(e, account)}
                    className={`
                        flex items-center gap-2 p-2 rounded-lg mb-1 cursor-pointer transition-all
                        ${isDragging ? 'opacity-50 bg-gray-100' : ''}
                        ${isDragOver && canDrop ? 'bg-indigo-100 border-2 border-indigo-500' : 'hover:bg-gray-50'}
                        ${account.isCategory ? 'bg-gray-50 dark:bg-slate-600 font-semibold' : 'bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600'}
                    `}
                >
                    {/* Drag Handle */}
                    {!account.isCategory && (
                        <GripVertical className="w-4 h-4 text-gray-400 cursor-grab flex-shrink-0" />
                    )}
                    
                    {/* Expand/Collapse Toggle */}
                    {hasChildren && !account.isCategory && (
                        <button
                            onClick={() => toggleExpand(account.id)}
                            className="p-1 hover:bg-gray-200 rounded"
                        >
                            {isExpanded ? (
                                <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                                <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                        </button>
                    )}
                    
                    {!hasChildren && !account.isCategory && <div className="w-5" />}

                    {/* Folder Icon */}
                    {account.isCategory ? (
                        <Folder className="w-5 h-5 text-gray-500 flex-shrink-0" />
                    ) : isExpanded && hasChildren ? (
                        <FolderOpen className="w-5 h-5 text-indigo-500 flex-shrink-0" />
                    ) : (
                        <Folder className="w-5 h-5 text-indigo-400 flex-shrink-0" />
                    )}

                    {/* Account Info */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <span className="font-mono text-sm text-gray-600 dark:text-gray-300">
                                {account.account_code}
                            </span>
                            <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                                {account.account_name}
                            </span>
                        </div>
                    </div>

                    {/* Level Badge */}
                    {!account.isCategory && (
                        <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-slate-600 rounded-full text-gray-500">
                            L{account.level}
                        </span>
                    )}

                    {/* Actions */}
                    {!account.isCategory && (
                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => handleEdit(account)}
                                className="p-1 text-indigo-600 hover:bg-indigo-50 rounded"
                            >
                                <Edit className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => handleDelete(account.id)}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Children */}
                {hasChildren && isExpanded && (
                    <div className="border-l-2 border-gray-200 dark:border-slate-500 ml-2">
                        {account.children.map(child => renderTreeItem(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Chart of Accounts</h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Drag and drop to reorganize accounts into 4-level hierarchy
                    </p>
                </div>
                <button 
                    onClick={() => { resetForm(); setShowModal(true); }} 
                    className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                    <Plus className="w-4 h-4 mr-2" /> Add Account
                </button>
            </div>

            {error && <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>}

            {/* Instructions */}
            <div className="mb-4 p-3 bg-blue-50 dark:bg-slate-700 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                    <strong>How to use:</strong> Click "Add Account" to create new accounts with just Name and Code. 
                    Then drag and drop accounts to arrange them in the hierarchy. 
                    Drop on an account to make it a child, or drop on a category to make it a top-level account.
                </p>
            </div>

            <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input 
                    type="text" 
                    placeholder="Search accounts..." 
                    value={searchTerm} 
                    onChange={(e) => setSearchTerm(e.target.value)} 
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
                />
            </div>

            {/* Hierarchy Tree View */}
            <div className="bg-white dark:bg-slate-700 rounded-lg shadow overflow-hidden">
                {loading ? (
                    <div className="p-8 text-center">
                        <RefreshCw className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
                    </div>
                ) : (
                    <div className="p-4">
                        {Object.entries(hierarchy).map(([type, category]) => (
                            <div key={type} className="mb-4">
                                {/* Category Header - Drop Zone */}
                                <div 
                                    onDragOver={(e) => draggedItem && handleDragOver(e, { ...category, level: 1 })}
                                    onDrop={(e) => handleDropToRoot(e, category)}
                                    className={`
                                        flex items-center gap-2 p-3 rounded-lg mb-2 
                                        bg-gray-100 dark:bg-slate-600 border-2 border-dashed
                                        ${dragOverItem?.id === category.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-300 dark:border-slate-500'}
                                    `}
                                >
                                    <Folder className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                                    <span className="font-semibold text-gray-700 dark:text-gray-200">
                                        {category.account_name}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        ({category.children.length} accounts)
                                    </span>
                                </div>
                                
                                {/* Child Accounts */}
                                <div className="space-y-1">
                                    {category.children.length > 0 ? (
                                        category.children.map(child => renderTreeItem(child, 0))
                                    ) : (
                                        <p className="text-sm text-gray-400 ml-9 italic">
                                            No accounts yet. Click "Add Account" to create one.
                                        </p>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modal - Simplified Form */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6 border-b border-gray-200 dark:border-slate-600">
                            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
                                {editingId ? 'Edit Account' : 'Add New Account'}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                Create account with Name and Code. Organize hierarchy using drag & drop.
                            </p>
                        </div>
                        
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            {/* Account Type */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Account Type <span className="text-red-500">*</span>
                                </label>
                                <select 
                                    value={formData.account_type} 
                                    onChange={(e) => setFormData({...formData, account_type: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                                    required
                                >
                                    <option value="">Select Type</option>
                                    {accountTypes.map(type => (
                                        <option key={type.value} value={type.value}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Account Code */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Account Code <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.account_code} 
                                    onChange={(e) => setFormData({...formData, account_code: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800" 
                                    placeholder="e.g., 1001, 4-10-01"
                                    required
                                />
                            </div>

                            {/* Account Name */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Account Name <span className="text-red-500">*</span>
                                </label>
                                <input 
                                    type="text" 
                                    value={formData.account_name} 
                                    onChange={(e) => setFormData({...formData, account_name: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800" 
                                    placeholder="e.g., Petty Cash, Cash at Bank"
                                    required
                                />
                            </div>

                            {/* Parent Account (Optional) */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Parent Account (Optional)
                                </label>
                                <select 
                                    value={formData.parent_account_id} 
                                    onChange={(e) => setFormData({...formData, parent_account_id: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800"
                                >
                                    <option value="">None (Top Level)</option>
                                    {accounts
                                        .filter(acc => acc.account_type === formData.account_type && acc.level < 4)
                                        .map(acc => (
                                            <option key={acc.id} value={acc.id}>
                                                {acc.account_code} - {acc.account_name} (Level {acc.level})
                                            </option>
                                        ))
                                    }
                                </select>
                                <p className="text-xs text-gray-500 mt-1">
                                    You can also skip this and organize using drag & drop later.
                                </p>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <textarea 
                                    value={formData.description} 
                                    onChange={(e) => setFormData({...formData, description: e.target.value})} 
                                    className="w-full px-3 py-2 border rounded-lg bg-white dark:bg-slate-800" 
                                    rows={2}
                                    placeholder="Optional description..."
                                />
                            </div>

                            {/* Active Toggle */}
                            <div className="flex items-center gap-2">
                                <input 
                                    type="checkbox" 
                                    id="is_active"
                                    checked={formData.is_active} 
                                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})} 
                                    className="w-4 h-4 rounded text-indigo-600"
                                />
                                <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
                                    Active Account
                                </label>
                            </div>
                            
                            <div className="mt-6 flex justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="px-4 py-2 border rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                                >
                                    {editingId ? 'Update' : 'Create'} Account
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChartOfAccounts;
